import React, { useMemo } from 'react';
import classNames from 'classnames/bind';
import { format } from 'date-fns';
import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { getLocalDate } from '@surfline/web-common';
import { TideTableIcon } from '@wavetrak/theme';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import type { GraphDataError } from 'types/graphs';
import type { TidePoint } from 'types/tide';
import type { Units } from 'types/units';

import styles from './TideReadingsTable.module.scss';

const cx = classNames.bind(styles);

const getTableClassName = (isTableMode: boolean = false) =>
  cx({
    [styles.isLiveTab]: !isTableMode,
    [styles.isTableMode]: isTableMode,
  });

export interface TideReadingsTableProps {
  tide: Array<TidePoint>;
  units?: Units;
  utcOffset?: number;
  error?: GraphDataError;
  loading?: boolean;
  isTableMode?: boolean;
  errorBoundaryClass?: string;
}

interface TideTableProps {
  tidePoints: Array<TidePoint>;
  loading?: boolean;
  units?: Units;
  utcOffset?: number;
  isTableMode?: boolean;
}

const tideIcon = (type: string) => {
  switch (type) {
    case 'HIGH':
      return <TideTableIcon direction="rising" />;
    case 'LOW':
      return <TideTableIcon direction="falling" />;
    default:
      return null;
  }
};

const TideReadingsTableSkeleton: React.FC<{ isTableMode?: boolean }> = ({
  isTableMode = false,
}) => (
  <Stack
    direction="column"
    divider={<Divider orientation="horizontal" />}
    spacing={isTableMode ? 0 : 2}
    data-testid="tide-readings-table-loading"
  >
    <Skeleton animation="wave" className={styles.skeleton} />
    <Skeleton animation="wave" className={styles.skeleton} />
    <Skeleton animation="wave" className={styles.skeleton} />
    <Skeleton animation="wave" className={styles.skeleton} />
  </Stack>
);

const TideTable = (props: TideTableProps) => {
  const { tidePoints, loading, utcOffset, units, isTableMode } = props;
  const isMobileView = useMaxWidthTablet();
  const theme = useTheme();
  const typographyVariant = isMobileView && !isTableMode ? 'body' : 'footnote';
  const typographyColor = isMobileView ? '' : theme.palette.text.secondary;

  return loading || !tidePoints ? (
    <TideReadingsTableSkeleton isTableMode={isTableMode} />
  ) : (
    <Table
      aria-label="Tide table"
      className={getTableClassName(isTableMode)}
      data-testid="tide-table"
    >
      <TableBody data-testid="tide-table-body">
        {tidePoints?.map((tidePoint) => {
          const roundedHeight = tidePoint?.height?.toFixed(1);

          const formattedDate = format(
            getLocalDate(tidePoint?.timestamp, tidePoint?.utcOffset ?? utcOffset),
            "h:mmaaaaa'm'",
          );
          return (
            <TableRow className={styles.tableRow} key={tidePoint.timestamp}>
              <TableCell
                className={styles.tableCell}
                align={!isTableMode ? 'right' : 'left'}
                width="20px"
              >
                <Box className={styles.icon}>{tideIcon(tidePoint?.type)}</Box>
              </TableCell>
              <TableCell className={styles.tableCell}>
                <Typography
                  className={styles.type}
                  variant={typographyVariant}
                  textTransform="capitalize"
                  color={isTableMode ? theme.palette.secondary.main : ''}
                >
                  {tidePoint?.type.toLowerCase()}
                </Typography>
              </TableCell>
              <TableCell className={styles.tableCell} align={!isTableMode ? 'left' : 'center'}>
                <Typography
                  className={styles.time}
                  variant={typographyVariant}
                  color={isTableMode ? theme.palette.secondary.main : ''}
                  textTransform="capitalize"
                >
                  {formattedDate}
                </Typography>
              </TableCell>
              <TableCell className={styles.tableCell} align={!isTableMode ? 'left' : 'right'}>
                <Typography
                  variant={typographyVariant}
                  color={!isTableMode ? typographyColor : theme.palette.secondary.main}
                >
                  {roundedHeight}
                  {units?.tideHeight.toLowerCase()}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export const TideReadingsTable: React.FC<TideReadingsTableProps> = ({
  tide,
  units,
  utcOffset,
  error,
  loading,
  errorBoundaryClass,
  isTableMode = false,
}) => {
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });
  const dailyHighsAndLows = useMemo(() => tide?.filter((point) => point.type !== 'NORMAL'), [tide]);

  if (!tide && !loading) {
    return null;
  }

  return (
    <Box data-testid="tide-readings-table">
      <ErrorBoundary
        error={hasError}
        errorBoundaryId="tide-readings-table"
        render={() => (
          <Box
            className={errorBoundaryClass}
            data-testid="tide-readings-table-error"
            style={isTableMode ? { height: 150, position: 'relative' } : {}}
          >
            <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
          </Box>
        )}
      >
        <TideTable
          tidePoints={dailyHighsAndLows}
          units={units}
          utcOffset={utcOffset}
          isTableMode={isTableMode}
          loading={loading}
        />
      </ErrorBoundary>
    </Box>
  );
};

export default React.memo(TideReadingsTable);
